import React from "react";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  CommunityBoundary,
  MonitoringConfigurationInput,
} from "src/Types/monitoring.types";
import { useTranslation } from "react-i18next";
import LocationSearch from "./LocationSearch";

interface LocationSetupPanelProps {
  draft: MonitoringConfigurationInput;
  message: { severity: "success" | "error"; text: string } | null;
  saving: boolean;
  canSave: boolean;
  compact?: boolean;
  onCommunitySelect: (community: CommunityBoundary) => void;
  onUpgrade: () => void;
  onSave: () => void;
}

const LocationSetupPanel: React.FC<LocationSetupPanelProps> = ({
  draft,
  message,
  saving,
  canSave,
  compact = false,
  onCommunitySelect,
  onUpgrade,
  onSave,
}) => {
  const { t } = useTranslation();
  return (
  <Stack spacing={2.5}>
    <Card sx={{ p: compact ? 2 : { xs: 2, sm: 2.5 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <MapOutlinedIcon color="primary" />
        <Box>
          <Typography variant="h6">{t("dashboard.setup.panelTitle")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("dashboard.setup.panelBody")}
          </Typography>
        </Box>
      </Stack>
    </Card>

    <Card sx={{ p: compact ? 2 : { xs: 2, sm: 2.5 }, overflow: "visible" }}>
      <Typography variant="h6" mb={0.5}>
        {t("dashboard.setup.findTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t("dashboard.setup.findBody")}
      </Typography>

      <LocationSearch onCommunitySelect={onCommunitySelect} />

      {draft.community && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: "rgba(52, 211, 153, 0.08)",
            border: "1px solid rgba(52, 211, 153, 0.25)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <CheckCircleIcon color="success" />
            <Box minWidth={0}>
              <Typography fontWeight={700}>
                {draft.community.name.split(",")[0]}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {draft.community.name}
              </Typography>
            </Box>
          </Stack>
          <Button
            size="small"
            startIcon={<AddLocationAltOutlinedIcon />}
            onClick={onUpgrade}
            sx={{ mt: 1.5 }}
          >
            {t("dashboard.setup.addAnother")}
          </Button>
        </Box>
      )}
    </Card>

    {message && <Alert severity={message.severity}>{message.text}</Alert>}
    <Button
      fullWidth
      size="large"
      variant="contained"
      disabled={!canSave || saving}
      onClick={onSave}
      startIcon={saving ? <CircularProgress size={18} /> : undefined}
    >
      {saving ? t("common.saving") : t("dashboard.setup.save")}
    </Button>
  </Stack>
  );
};

export default LocationSetupPanel;
